import { FC, useMemo } from 'react';
import { ChatMessage } from '../../AIChat/types';
import ActionCard from '../../AIChat/components/ActionCard';
import StreamingIndicator from '../../AIChat/components/StreamingIndicator';
import { cleanRecommendationMarkers } from '../../AIChat/utils/parsers';

interface ChatMessageBubbleProps {
  message: ChatMessage;
}

const ChatMessageBubble: FC<ChatMessageBubbleProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isTool = message.role === 'tool';

  const displayContent = useMemo(() => {
    if (!message.content) return '';
    return cleanRecommendationMarkers(message.content);
  }, [message.content]);

  const formattedContent = useMemo(() => {
    if (!displayContent) return null;

    const lines = displayContent.split('\n');
    const elements: JSX.Element[] = [];

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      if (trimmed.startsWith('### ')) {
        elements.push(
          <h4 key={index} className="font-semibold text-sm mt-3 mb-1 text-inherit">
            {trimmed.slice(4)}
          </h4>,
        );
        return;
      }
      if (trimmed.startsWith('## ')) {
        elements.push(
          <h3 key={index} className="font-semibold text-base mt-3 mb-1 text-inherit">
            {trimmed.slice(3)}
          </h3>,
        );
        return;
      }

      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        elements.push(
          <li key={index} className="ml-4 text-sm text-inherit">
            {formatInlineMarkdown(trimmed.slice(2))}
          </li>,
        );
        return;
      }

      const numberedMatch = trimmed.match(/^(\d+)\.\s(.+)/);
      if (numberedMatch) {
        elements.push(
          <li key={index} className="ml-4 text-sm list-decimal text-inherit">
            {formatInlineMarkdown(numberedMatch[2])}
          </li>,
        );
        return;
      }

      if (!trimmed) {
        elements.push(<div key={index} className="h-2" />);
        return;
      }

      elements.push(
        <p key={index} className="text-sm text-inherit leading-relaxed">
          {formatInlineMarkdown(trimmed)}
        </p>,
      );
    });

    return elements;
  }, [displayContent]);

  if (isTool) return null;

  const hasToolCalls = message.toolCalls && message.toolCalls.length > 0;
  const isExecutingTools = hasToolCalls && message.isStreaming;

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      {isAssistant && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-vine dark:bg-teal-600 flex items-center justify-center mr-3 mt-1">
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
        </div>
      )}
      <div
        className={`max-w-[80%] rounded-2xl px-5 py-3.5 ${
          isUser
            ? 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white rounded-br-sm'
            : 'bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-gray-100 rounded-bl-sm border border-gray-200 dark:border-slate-700'
        }`}
      >
        {isAssistant && message.isStreaming ? (
          <StreamingIndicator text={isExecutingTools ? 'Fetching data' : undefined} />
        ) : (
          <>
            {hasToolCalls && !message.isStreaming && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {message.toolCalls?.map((tc) => (
                  <span
                    key={tc.id}
                    className="inline-flex items-center px-2 py-1 rounded-lg text-xs bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400 font-medium"
                  >
                    <svg
                      className="w-3 h-3 mr-1.5 text-orange dark:text-teal-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    {formatToolName(tc.name)}
                  </span>
                ))}
              </div>
            )}

            <div className="space-y-1.5">{formattedContent}</div>
          </>
        )}

        {isAssistant && message.recommendations && message.recommendations.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-600">
            {message.recommendations.map((rec, index) => (
              <ActionCard key={index} recommendation={rec} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const formatInlineMarkdown = (text: string): React.ReactNode => {
  const parts: React.ReactNode[] = [];
  let keyIndex = 0;

  const boldRegex = /\*\*([^*]+)\*\*/g;
  const codeRegex = /`([^`]+)`/g;

  let match: RegExpExecArray | null;

  const boldParts: Array<{ start: number; end: number; content: React.ReactNode }> = [];
  match = boldRegex.exec(text);
  while (match) {
    const boldKey = `bold-${keyIndex}`;
    keyIndex += 1;
    boldParts.push({
      start: match.index,
      end: match.index + match[0].length,
      content: (
        <strong key={boldKey} className="font-semibold">
          {match[1]}
        </strong>
      ),
    });
    match = boldRegex.exec(text);
  }

  const codeParts: Array<{ start: number; end: number; content: React.ReactNode }> = [];
  match = codeRegex.exec(text);
  while (match) {
    const codeKey = `code-${keyIndex}`;
    keyIndex += 1;
    codeParts.push({
      start: match.index,
      end: match.index + match[0].length,
      content: (
        <code key={codeKey} className="px-1.5 py-0.5 bg-gray-200 dark:bg-slate-600 rounded text-xs font-mono">
          {match[1]}
        </code>
      ),
    });
    match = codeRegex.exec(text);
  }

  const allParts = [...boldParts, ...codeParts].sort((a, b) => a.start - b.start);

  if (allParts.length === 0) return text;

  let lastIndex = 0;
  for (const part of allParts) {
    if (part.start > lastIndex) {
      parts.push(text.slice(lastIndex, part.start));
    }
    parts.push(part.content);
    lastIndex = part.end;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <>{parts}</>;
};

const formatToolName = (name: string): string => {
  return name
    .replace(/_/g, ' ')
    .replace(/^get /, '')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default ChatMessageBubble;
